import { type SyntheticEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  displayColumnName,
  firstOrdinaryColumn,
  LabelChipPicker,
  useBoardDetail,
  useDestination,
  useCreateCalendarCard,
} from '@/entities/board';
import {
  AssigneePicker,
  type AssignmentDraft,
  NO_ASSIGNMENT,
  useSetAssignment,
} from '@/entities/collab';
import {
  useClearQuickCreateDefaults,
  useQuickCreateDefaults,
  useSetQuickCreateDefaults,
} from '@/entities/workspace';
import { useErrorMessage } from '@/shared/i18n';
import {
  type DeadlineDraft,
  type DeadlineErrorCode,
  rangeToPayload,
  validateRange,
} from '@/shared/lib/deadline-draft';
import {
  Button,
  Combobox,
  DeadlineFields,
  Dialog,
  ErrorState,
  LoadingState,
  useToast,
} from '@/shared/ui';

import styles from './QuickCreate.module.css';

const CARD_TITLE_MAX = 500;

/** A workspace the task can be created in (offered by the workspace choice). */
export interface TaskWorkspace {
  id: string;
  name: string;
}

interface ContentProps {
  /** `null`: a guest of this board alone (the board is then always fixed). */
  workspaceId: string | null;
  timezone: string | null;
  /** Create on this board only (board page): no board choice, its default column is preselected. */
  fixedBoardId?: string;
  onClose: () => void;
}

const EMPTY_DEADLINE: DeadlineDraft = {
  hasStart: false,
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
};

/**
 * Workspace-wide "Create task": title, destination (board, column) and an optional due date.
 * It creates an ordinary card at the end of the chosen column, nothing else. The destination
 * starts at the user's configured default (if still valid) and can always be changed; it is
 * remembered only when "Make this the default" is ticked.
 */
function QuickCreateContent({ workspaceId, timezone, fixedBoardId, onClose }: ContentProps) {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const toast = useToast();
  const navigate = useNavigate();
  const id = useId();
  const destination = useDestination({
    workspaceId,
    ...(fixedBoardId !== undefined && { fixedBoardId }),
    boardChoice: null,
    columnChoice: null,
  });
  const boards = destination.boards;
  const defaults = useQuickCreateDefaults(
    workspaceId ?? '',
    workspaceId !== null && fixedBoardId === undefined,
  );
  const create = useCreateCalendarCard();
  const setDefaults = useSetQuickCreateDefaults(workspaceId ?? '');
  const clearDefaults = useClearQuickCreateDefaults(workspaceId ?? '');
  const titleInput = useRef<HTMLInputElement>(null);
  // The dialog focuses its close button when it opens; the title is what the user types next.
  useEffect(() => {
    const timer = window.setTimeout(() => titleInput.current?.focus());
    return () => {
      window.clearTimeout(timer);
    };
  }, []);
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState(false);
  const [boardChoice, setBoardChoice] = useState<string | null>(null);
  const [columnChoice, setColumnChoice] = useState<string | null>(null);
  const [labelChoice, setLabelChoice] = useState<string[]>([]);
  const [withDeadline, setWithDeadline] = useState(false);
  const [deadline, setDeadline] = useState<DeadlineDraft>(EMPTY_DEADLINE);
  const [deadlineErrors, setDeadlineErrors] = useState<DeadlineErrorCode[]>([]);
  const [makeDefault, setMakeDefault] = useState(false);
  const [assignment, setAssignment] = useState<AssignmentDraft>(NO_ASSIGNMENT);

  const boardList = destination.boardList;
  // Explicit choice > the configured default board > the first board.
  const boardId =
    boardList.find((board) => board.id === boardChoice)?.id ??
    boardList.find((board) => board.id === defaults.data?.default_board_id)?.id ??
    boardList[0]?.id ??
    '';
  const board = boardList.find((candidate) => candidate.id === boardId);
  const detail = useBoardDetail(boardId, boardId !== '');
  const columns = detail.data?.columns ?? [];
  // Explicit choice > configured default column (same board) > the board's default > the first.
  const configuredColumn =
    fixedBoardId === undefined && defaults.data?.default_board_id === boardId
      ? defaults.data.default_column_id
      : null;
  const columnId =
    columns.find((column) => column.id === columnChoice)?.id ??
    columns.find((column) => column.id === configuredColumn)?.id ??
    columns.find((column) => column.id === board?.default_column_id)?.id ??
    firstOrdinaryColumn(columns)?.id ??
    '';
  // Only labels of the board being created on count (switching boards drops the others).
  const boardLabels = detail.data?.labels ?? [];
  const labelIds = labelChoice.filter((labelId) => boardLabels.some((l) => l.id === labelId));
  const hasDefault = defaults.data?.default_board_id != null;
  const assign = useSetAssignment(boardId);
  const mayAssign = detail.data?.permissions.includes('task.assign') ?? false;

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const trimmed = title.trim();
    const found = withDeadline ? validateRange(deadline) : [];
    setTitleError(trimmed === '');
    setDeadlineErrors(found);
    if (trimmed === '' || found.length > 0 || !columnId) return;
    create.mutate(
      {
        columnId,
        title: trimmed,
        deadline: withDeadline ? rangeToPayload(deadline) : null,
        labelIds,
      },
      {
        onSuccess: (card) => {
          if (mayAssign && assignment.kind !== 'unassigned') {
            assign.mutate({
              cardId: card.id,
              kind: assignment.kind,
              assigneeIds: assignment.kind === 'assigned' ? assignment.ids : [],
            });
          }
          if (makeDefault && fixedBoardId === undefined)
            setDefaults.mutate({ board_id: boardId, column_id: columnId });
          toast.show({
            message: t('quickCreate.created', { board: board?.name ?? '' }),
            actionLabel: t('toast.open'),
            onAction: () => Promise.resolve(navigate(`/app/boards/${boardId}/cards/${card.id}`)),
          });
          onClose();
        },
      },
    );
  };
  let body;
  if (boards.isPending || (fixedBoardId === undefined && defaults.isPending)) {
    body = <LoadingState />;
  } else if (boards.isError) body = <ErrorState error={boards.error} />;
  else if (boardList.length === 0) body = <p>{t('quickCreate.noBoards')}</p>;
  else {
    body = (
      <form className={styles.form} noValidate onSubmit={submit} data-testid="quick-create">
        <div className={styles.field}>
          <label htmlFor={`${id}-title`} className={styles.label}>
            {t('cards.title')}
          </label>
          <input
            id={`${id}-title`}
            ref={titleInput}
            className={styles.input}
            value={title}
            maxLength={CARD_TITLE_MAX}
            autoComplete="off"
            aria-invalid={titleError}
            onChange={(event) => {
              setTitle(event.target.value);
              setTitleError(false);
            }}
          />
          {titleError && (
            <span role="alert" className={styles.error}>
              {t('validation.REQUIRED')}
            </span>
          )}
        </div>

        <div className={styles.destination}>
          {fixedBoardId === undefined && (
            <div className={styles.field}>
              <label htmlFor={`${id}-board`} className={styles.label}>
                {t('calendar.board')}
              </label>
              <Combobox
                id={`${id}-board`}
                label={t('calendar.board')}
                value={boardId}
                options={boardList.map((option) => ({ value: option.id, label: option.name }))}
                onChange={(next) => {
                  setBoardChoice(next);
                  setColumnChoice(null);
                  setLabelChoice([]);
                  setAssignment(NO_ASSIGNMENT);
                }}
              />
            </div>
          )}
          <div className={styles.field}>
            <label htmlFor={`${id}-column`} className={styles.label}>
              {t('calendar.column')}
            </label>
            <Combobox
              id={`${id}-column`}
              label={t('calendar.column')}
              value={columnId}
              disabled={detail.isPending || columns.length === 0}
              options={columns.map((column) => ({
                value: column.id,
                label: displayColumnName(column, t('columns.calendar')),
              }))}
              onChange={setColumnChoice}
            />
            {detail.isSuccess && columns.length === 0 && (
              <span role="alert" className={styles.error}>
                {t('calendar.noColumns')}
              </span>
            )}
          </div>
        </div>

        {boardLabels.length > 0 && (
          <div className={styles.field}>
            <span className={styles.label} id={`${id}-labels`}>
              {t('labels.title')}
            </span>
            <LabelChipPicker
              labels={boardLabels}
              selectedIds={labelIds}
              label={t('labels.title')}
              emptyText={t('labels.pickerEmpty')}
              disabled={create.isPending}
              onToggle={(label, selected) => {
                setLabelChoice(
                  selected
                    ? [...labelIds.filter((x) => x !== label.id), label.id]
                    : labelIds.filter((x) => x !== label.id),
                );
              }}
            />
          </div>
        )}

        {mayAssign && (
          <AssigneePicker
            boardId={boardId}
            value={assignment}
            disabled={create.isPending}
            onChange={setAssignment}
          />
        )}

        <label className={styles.check}>
          <input
            type="checkbox"
            checked={withDeadline}
            onChange={(event) => {
              setWithDeadline(event.target.checked);
              setDeadlineErrors([]);
            }}
          />
          {t('quickCreate.setDeadline')}
        </label>
        {withDeadline && (
          <DeadlineFields
            value={deadline}
            errors={deadlineErrors}
            timezone={timezone}
            onChange={(next) => {
              setDeadline(next);
              setDeadlineErrors([]);
            }}
          />
        )}

        {fixedBoardId === undefined && (
          <div className={styles.defaultRow}>
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={makeDefault}
                onChange={(event) => {
                  setMakeDefault(event.target.checked);
                }}
              />
              {t('quickCreate.makeDefault')}
            </label>
            {hasDefault && (
              <Button
                size="sm"
                variant="ghost"
                disabled={clearDefaults.isPending}
                onClick={() => {
                  clearDefaults.mutate();
                }}
              >
                {t('quickCreate.clearDefault')}
              </Button>
            )}
          </div>
        )}

        {create.isError && (
          <p role="alert" className={styles.error}>
            {getErrorMessage(create.error)}
          </p>
        )}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={create.isPending || !columnId}>
            {t('quickCreate.create')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    );
  }
  return body;
}

interface Props extends ContentProps {
  /**
   * Home: the workspaces where the viewer may create tasks. Shows a searchable workspace choice
   * above the board choice (the board list always belongs to the chosen workspace).
   */
  workspaces?: readonly TaskWorkspace[];
}

/**
 * The create-task dialog. On a board or workspace page the destination is fixed by the page; from
 * Home one dialog serves every workspace: pick the workspace, then the board and column.
 */
export function QuickCreateDialog({ workspaces, ...content }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const [chosen, setChosen] = useState(content.workspaceId);
  const workspaceId = workspaces ? chosen : content.workspaceId;
  return (
    <Dialog title={t('quickCreate.title')} onClose={content.onClose}>
      {workspaces && workspaces.length > 1 && (
        <div className={styles.field}>
          <label htmlFor={`${id}-workspace`} className={styles.label}>
            {t('quickCreate.workspace')}
          </label>
          <Combobox
            id={`${id}-workspace`}
            label={t('quickCreate.workspace')}
            value={workspaceId ?? ''}
            options={workspaces.map((option) => ({ value: option.id, label: option.name }))}
            onChange={setChosen}
          />
        </div>
      )}
      {/* A new workspace starts a clean form: its boards, columns, labels and defaults differ. */}
      <QuickCreateContent key={workspaceId ?? ''} {...content} workspaceId={workspaceId} />
    </Dialog>
  );
}
